import { message } from 'antd';
import { create } from 'zustand';

import { subscriptionDetailList, usageDetail } from '@/service/subscription';
import { type CurrentPlanDetail, EPlanAlias, type PackageDetailInfo } from '@/types/subscription';

export interface SubscriptionStore {
  loading: boolean;
  currentPlanDetail: CurrentPlanDetail | undefined;
  priceList: PackageDetailInfo[];
  computed: {
    // 把业务相关数据脏逻辑全部在这里弄好，方便多端复用
    maxFileMBSize: number;
    memoriesUsageString: string;
    memoriesPercent: number;
    memoriesUsageStringMenu: string;
    messagesPercent: number;
    messagesUsageString: string;
    messagesUsageStringMenu: string;
    talksPercent: number;
    talksUsageString: string;
    talksUsageStringMenu: string;
  };
  updateUsage: () => void;
  updatePriceList: () => void;
}

const useSubscription = create<SubscriptionStore>()((set, get) => ({
  currentPlanDetail: undefined,
  priceList: [],
  loading: false,
  computed: {
    get maxFileMBSize() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return 10;

      return [EPlanAlias.PLUS, EPlanAlias.PRO].includes(currentPlanDetail.currentPlanAlias)
        ? 50
        : 10;
    },
    get memoriesUsageString() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return '';

      return currentPlanDetail.total.memories == -1 || currentPlanDetail.remain.memories == -1
        ? 'menu.subscriptionModal.unlimited'
        : `${Math.max(currentPlanDetail.remain.memories, 0)} / ${currentPlanDetail.total.memories}`;
    },
    get memoriesUsageStringMenu() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return '';

      return currentPlanDetail.total.memories == -1 || currentPlanDetail.remain.memories == -1
        ? 'menu.subscriptionModal.unlimited'
        : `${Math.max(currentPlanDetail.remain.memories, 0)}`;
    },
    get memoriesPercent() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return 0;

      return currentPlanDetail.total.memories == -1 || currentPlanDetail.remain.memories == -1
        ? 100
        : (currentPlanDetail.remain.memories / currentPlanDetail.total.memories) * 100;
    },
    get messagesPercent() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return 0;

      // Only Free needs to be calculated
      return [EPlanAlias.PLUS, EPlanAlias.PRO].includes(currentPlanDetail.currentPlanAlias) ||
        currentPlanDetail.total.gpt35messages == -1 ||
        currentPlanDetail.remain.gpt4messages == -1 //领取活动奖励的特殊
        ? 100
        : ((currentPlanDetail.remain.gpt35messages + currentPlanDetail.remain.freeMessages) /
            currentPlanDetail.total.gpt35messages) *
            100;
    },
    get messagesUsageString() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return '';

      // PRO
      // if (currentPlanDetail.remain.gpt4messages == -1) return 'Unlimited with GPT-4';
      if (currentPlanDetail.remain.gpt4messages == -1) return 'menu.subscriptionModal.unlimited';

      // free messages actually equal to GPT-4
      const gpt4Num = currentPlanDetail.remain.gpt4messages + currentPlanDetail.remain.freeMessages;

      const gpt3Num =
        currentPlanDetail.remain.gpt35messages === -1
          ? 'unlimited'
          : currentPlanDetail.remain.gpt35messages;

      // return (gpt4Num ? `${gpt4Num} with GPT-4 + ` : '') + `${gpt3Num} with GPT-3.5`;

      return gpt3Num === 'unlimited'
        ? `menu.subscriptionModal.unlimited`
        : `${(gpt4Num || 0) + gpt3Num}`;
    },
    get messagesUsageStringMenu() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return '';

      return [EPlanAlias.PLUS, EPlanAlias.PRO].includes(currentPlanDetail.currentPlanAlias) ||
        currentPlanDetail.total.gpt35messages == -1 ||
        currentPlanDetail.remain.gpt4messages == -1 //领取活动奖励的特殊
        ? 'menu.subscriptionModal.unlimited'
        : `${
            currentPlanDetail.remain.gpt35messages +
            currentPlanDetail.remain.freeMessages +
            currentPlanDetail.remain.gpt4messages
          }`;
    },
    get talksPercent() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return 0;

      return currentPlanDetail.total.identityOutputs == -1 ||
        currentPlanDetail.remain.identityOutputs == -1
        ? 100
        : (currentPlanDetail.remain.identityOutputs / currentPlanDetail.total.identityOutputs) *
            100;
    },
    get talksUsageString() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return '';

      return currentPlanDetail.total.identityOutputs == -1 ||
        currentPlanDetail.remain.identityOutputs == -1
        ? 'menu.subscriptionModal.unlimited'
        : `${Math.max(currentPlanDetail.remain.identityOutputs, 0)} / ${
            currentPlanDetail.total.identityOutputs
          }`;
    },
    get talksUsageStringMenu() {
      const { currentPlanDetail } = get();

      if (!currentPlanDetail) return '';

      return currentPlanDetail.total.identityOutputs == -1 ||
        currentPlanDetail.remain.identityOutputs == -1
        ? 'menu.subscriptionModal.unlimited'
        : `${Math.max(currentPlanDetail.remain.identityOutputs, 0)}`;
    }
  },
  updateUsage: () => {
    set({ loading: true });

    usageDetail()
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || 'Oops. Something went wrong.');
        }

        const currentPlanDetail = res.data.data;

        set({ currentPlanDetail });
      })
      .catch((error) => {
        console.error(error?.message ?? 'Service error');
        message.error(error?.message ?? 'Service error');
      })
      .finally(() => {
        set({ loading: false });
      });
  },
  updatePriceList: () => {
    subscriptionDetailList()
      .then((dataList) => {
        if (dataList.data.code !== 0) {
          throw Error(dataList.data.message || 'Oops. Something went wrong.');
        }

        const priceList = dataList.data.data;

        set({ priceList });
      })
      .catch((error) => {
        console.error(error?.message ?? 'Service error');
        message.error(error?.message ?? 'Service error');
      });
  }
}));

export default useSubscription;
